import { Component, Event, EventEmitter, Prop, State, Watch, forceUpdate, h } from '@stencil/core';
import { IChat, IMessage, sendMessage } from '../../backend/chatService';
import { i18nProvider } from '@trumpf/ux.i18n';
import loadTranslations, { TranslationKey, TranslationOptions } from '../../utils/loadTranslations';

@Component({
  tag: 'ai-chat',
  styleUrl: 'ai-chat.css',
  shadow: true,
})
export class AiChat {
  @Prop() token: string;
  @Prop({ mutable: true }) data: IChat;
  @Prop() caseId: string;
  @Prop() caseStatus: string;
  @State() messageText = '';
  @State() isAiTyping = false;
  @Event() disclaimerSubmitted: EventEmitter<void>;
  private i18nSubscriberId: string;

  @Prop({ reflect: true, mutable: true }) locale: string = 'de-DE';

  private handleKeyPress = (event: CustomEvent<{ key: string; value: string }>) => {
    if (event.detail.key === 'Enter') {
      const value = event.detail.value;

      if (value && value.trim()) {
        const customEvent = new CustomEvent('usMessageSend', {
          detail: value.trim(),
        });
        this.handleMessageSend(customEvent);
      }
    }
  };

  private async handleMessageSend(event: CustomEvent<string>) {
    const messageText = event.detail;

    // Create message with correct schema
    const userMessage: IMessage = {
      source: 'user',
      message: messageText,
      timestamp: new Date().toISOString(),
    };

    console.log('Sending message to server:', userMessage);

    // Set typing state and send to server
    this.isAiTyping = true;

    try {
      // Update the local messages array with the user message immediately
      this.data = {
        ...this.data,
        messages: [...this.data.messages, userMessage],
      };

      const aiResponse = await sendMessage(this.caseId, this.data.sessionId, userMessage);
      console.log('Received response from server:', aiResponse);

      // Update the local messages array with the AI response
      this.data = {
        ...this.data,
        messages: [...this.data.messages, aiResponse],
      };

      // Remove typing indicator
      this.isAiTyping = false;
    } catch (error) {
      console.error('Error getting AI response:', error);
      this.isAiTyping = false;
    }
  }

  connectedCallback() {
    this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
  }

  disconnectedCallback() {
    i18nProvider.unsubscribe(this.i18nSubscriberId);
  }

  componentWillLoad() {
    this.loadTranslations();
  }

  @Watch('locale')
  onLocaleChange() {
    if (this.locale !== i18nProvider.language) {
      this.loadTranslations();
    }
  }

  private loadTranslations() {
    loadTranslations(this.locale).then(locale => {
      i18nProvider.changeLanguage(locale);
      this.locale = locale;
    });
  }

  private async handleDisclaimerSubmitted() {
    console.log('Disclaimer submitted event received');
    this.disclaimerSubmitted.emit();
  }

  render() {
    if (!this.token) {
      return <div class="error">Error: Token is missing or invalid.</div>;
    }

    console.log('Case status inside:', this.caseStatus);
    const t = (key: TranslationKey, options: TranslationOptions = { returnObjects: true }) => i18nProvider.t(`ai-in-service:${String(key)}`, options);
    let contentArray: string[] = [];
    try {
      const rawContent = t('legalDisclaimer.content');
      contentArray = Array.isArray(rawContent) ? rawContent : JSON.parse(rawContent);
    } catch (error) {
      console.error('Failed to parse content array:', error);
    }

    return (
      <div class="ai-chat-container">
        <ux-chat typing={this.isAiTyping ? 'TRUMPF AI Assistant is typing' : undefined}>
          {!this.data.messages || this.data.messages.length === 0 ? (
            <legal-disclaimer
              headline={t('legalDisclaimer.headline')}
              button-label={t('legalDisclaimer.buttonLabel')}
              checkbox-label={t('legalDisclaimer.checkboxLabel')}
              onDisclaimerSubmitted={() => this.handleDisclaimerSubmitted()}
            >
              {contentArray.map((content, index) => (
                <ux-copy variant="default" key={index}>
                  {index === 1 ? <strong>{content}</strong> : content}
                </ux-copy>
              ))}
            </legal-disclaimer>
          ) : (
            this.data.messages?.map((message, index) => (
              <ux-chat-item
                key={index}
                source={message.source}
                name={message.source === 'bot' || message.source === 'system' ? 'TRUMPF AI Assistent' : message.name}
                date={message.timestamp}
              >
                <ux-copy>{message.message}</ux-copy>
                {message.source === 'system' && <ux-button id={`feedback-button-${index}`}>Give feedback</ux-button>}
              </ux-chat-item>
            ))
          )}
          <ux-chat-message-bar
            placeholder="Ihre Nachricht"
            slot="bar"
            onUsMessageSend={event => this.handleMessageSend(event)}
            onUsKeyPress={this.handleKeyPress}
            disabled={this.caseStatus === 'closed'}
          ></ux-chat-message-bar>
        </ux-chat>
      </div>
    );
  }
}
